/**
 * Enum representing user roles in the system.
 * Each role defines a set of permissions for accessing resources.
 *
 * @enum {string}
 * @property {string} SUPER_ADMIN - Super Admin role with full access.
 * @property {string} INSPECTOR - Inspector role with permissions related to inspections and estimates.
 * @property {string} PROVEN_INSPECTOR - Proven Inspector role with extended permissions related to inspections and estimates.
 * Training / audit roles
 * @property {string} TRAINEE_INSPECTOR - Trainee Inspector role with permissions for viewing and creating inspections, as well as viewing estimates.
 * @property {string} AUDITOR - Auditor role with permissions for managing audits, including creation, viewing, updating, and deletion.
 * Basic roles
 * Dealers roles
 * @property {string} COMPANY_ROOT - Company Root role with permissions for managing company-wide operations.
 * @property {string} COMPANY_ADMIN - Company Admin role. Company-wide like COMPANY_ROOT and able to create
 * other Company Admins, but ranks below it and carries no deletion immunity.
 * @property {string} COMPANY_LOCATION_MANAGER - Company Location Manager role with permissions for managing locations and inspections at specific locations.
 * @property {string} COMPANY_STANDARD_USER - Company Standard User role with basic permissions.
 * @property {string} COMPANY_ADVANCED_USER - Company Advanced User role with additional permissions for managing reports and notifications.
 * @property {string} COMPANY_CUSTOMS_USER - Company Customs User role with defined permissions.
 * Leasing roles
 * @property {string} COMPANY_READ_ONLY_USER - Company Read-Only User role with permissions to view inspections and locations.
 * @property {string} COMPANY_INSPECTOR_COORDINATOR - Company Inspector Coordinator role with permissions to manage inspections and coordinate inspectors and reports.
 * Flooring roles
 * @property {string} FLOORING_DEALERSHIP_MANAGER - Flooring Dealership Manager role with permissions for viewing audits and locations, as well as managing car-related operations in the flooring dealership.
 * @property {string} FLOORING_FINANCE_MANAGER - Flooring Finance Manager role with permissions to view audits and locations, focusing on financial oversight within the flooring division.
 * @property {string} FLOORING_MANAGER - Flooring Manager role with permissions to view and update audits, as well as view locations, focusing on operational management within the flooring sector.
 * @property {string} FLOORING_AUDITOR - Flooring Auditor role with permissions to manage locations, users, and cars, including creation, viewing, updating, and deletion within the flooring division.
 */
export enum UserRole {
  SUPER_ADMIN = 'SUPER_ADMIN',
  INSPECTOR = 'INSPECTOR',
  PROVEN_INSPECTOR = 'PROVEN_INSPECTOR',
  // Training / audit roles
  TRAINEE_INSPECTOR = 'TRAINEE_INSPECTOR',
  AUDITOR = 'AUDITOR',
  // Basic roles
  COMPANY_ROOT = 'COMPANY_ROOT',
  COMPANY_ADMIN = 'COMPANY_ADMIN',
  COMPANY_LOCATION_MANAGER = 'COMPANY_LOCATION_MANAGER',
  COMPANY_STANDARD_USER = 'COMPANY_STANDARD_USER',
  COMPANY_ADVANCED_USER = 'COMPANY_ADVANCED_USER',
  COMPANY_CUSTOMS_USER = 'COMPANY_CUSTOMS_USER',
  // Leasing roles
  COMPANY_READ_ONLY_USER = 'COMPANY_READ_ONLY_USER',
  COMPANY_INSPECTOR_COORDINATOR = 'COMPANY_INSPECTOR_COORDINATOR',
  // Flooring roles
  FLOORING_DEALERSHIP_MANAGER = 'FLOORING_DEALERSHIP_MANAGER',
  FLOORING_FINANCE_MANAGER = 'FLOORING_FINANCE_MANAGER',
  FLOORING_MANAGER = 'FLOORING_MANAGER',
  FLOORING_AUDITOR = 'FLOORING_AUDITOR',
}

/**
 * Enum representing various permissions in the system.
 * Each permission corresponds to a specific operation that can be performed on a resource.
 *
 * @enum {string}
 * @property {string} AC - Permission to create audits.
 * @property {string} AV - Permission to view audits.
 * @property {string} AU - Permission to update audits.
 * @property {string} AD - Permission to delete audits.
 * @property {string} CRC - Permission to create cars.
 * @property {string} CRV - Permission to view cars.
 * @property {string} CRU - Permission to update cars.
 * @property {string} CRD - Permission to delete cars.
 * @property {string} IV - Permission to view inspections.
 * @property {string} IC - Permission to create inspections.
 * @property {string} IU - Permission to update inspections.
 * @property {string} ID - Permission to delete inspections.
 * @property {string} RS - Permission to share reports.
 * @property {string} RAm - Permission to report aftermarket modify.
 * @property {string} FC - Permission to create files.
 * @property {string} TV - Permission to view templates.
 * @property {string} TC - Permission to create templates.
 * @property {string} TU - Permission to update templates.
 * @property {string} TD - Permission to delete templates.
 * @property {string} UV - Permission to view users.
 * @property {string} UC - Permission to create users.
 * @property {string} UU - Permission to update users.
 * @property {string} UD - Permission to delete users.
 * @property {string} CC - Permission to create companies.
 * @property {string} CV - Permission to view companies.
 * @property {string} CU - Permission to update companies.
 * @property {string} CD - Permission to delete companies.
 * @property {string} LV - Permission to view locations.
 * @property {string} LC - Permission to create locations.
 * @property {string} LU - Permission to update locations.
 * @property {string} LD - Permission to delete locations.
 * @property {string} IFC - Permission to create inspection flows.
 * @property {string} IFU - Permission to update inspection flows.
 * @property {string} IFD - Permission to delete inspection flows.
 * @property {string} IFV - Permission to view inspection flows.
 * @property {string} IFA - Permission to assign inspection flows.
 * @property {string} IFmM - Permission to modify masks in inspection flows.
 * @property {string} IFmP - Permission to modify panels in inspection flows.
 * @property {string} EV - Permission to view estimates.
 * @property {string} EC - Permission to create estimates.
 * @property {string} EU - Permission to update estimates.
 * @property {string} EA - Permission to approve estimates.
 * @property {string} SV - Permission to view settings.
 * @property {string} SU - Permission to update settings.
 * @property {string} EMv - Permission to modify vehicles in estimates.
 * @property {string} EMp - Permission to modify panels in estimates.
 */
export enum PermissionEnum {
  AC = 'AUDITS_CREATE',
  AV = 'AUDITS_VIEW',
  AU = 'AUDITS_UPDATE',
  AD = 'AUDITS_DELETE',
  CRC = 'CARS_CREATE',
  CRV = 'CARS_VIEW',
  CRU = 'CARS_UPDATE',
  CRD = 'CARS_DELETE',
  IV = 'INSPECTIONS_VIEW',
  IC = 'INSPECTIONS_CREATE',
  IU = 'INSPECTIONS_UPDATE',
  ID = 'INSPECTIONS_DELETE',
  RS = 'REPORTS_SHARE',
  RAm = 'REPORTS_AFTERMARKET_MODIFY',
  FC = 'FILES_CREATE',
  TV = 'TEMPLATES_VIEW',
  TC = 'TEMPLATES_CREATE',
  TU = 'TEMPLATES_UPDATE',
  TD = 'TEMPLATES_DELETE',
  UV = 'USERS_VIEW',
  UC = 'USERS_CREATE',
  UU = 'USERS_UPDATE',
  UD = 'USERS_DELETE',
  CC = 'COMPANIES_CREATE',
  CV = 'COMPANIES_VIEW',
  CU = 'COMPANIES_UPDATE',
  CD = 'COMPANIES_DELETE',
  LV = 'LOCATIONS_VIEW',
  LC = 'LOCATIONS_CREATE',
  LU = 'LOCATIONS_UPDATE',
  LD = 'LOCATIONS_DELETE',
  IFC = 'INSPECTION_FLOWS_CREATE',
  IFU = 'INSPECTION_FLOWS_UPDATE',
  IFD = 'INSPECTION_FLOWS_DELETE',
  IFV = 'INSPECTION_FLOWS_VIEW',
  IFA = 'INSPECTION_FLOWS_ASSIGN',
  IFmM = 'INSPECTION_FLOWS_MODIFY_MASKS',
  IFmP = 'INSPECTION_FLOWS_MODIFY_PANELS',
  EV = 'ESTIMATES_VIEW',
  EC = 'ESTIMATES_CREATE',
  EU = 'ESTIMATES_UPDATE',
  EA = 'ESTIMATES_APPROVE',
  SV = 'SETTINGS_VIEW',
  SU = 'SETTINGS_UPDATE',
  EMv = 'ESTIMATES_MODIFY_VEHICLES',
  EMp = 'ESTIMATES_MODIFY_PANELS',
}

/**
 * Enum representing sort options for role inputs.
 * @enum {string}
 */
export enum SortByForRoleInputType {
  id = 'id',
  createdAt = 'createdAt',
  name = 'name',
}

/**
 * Enum for resources that are used in the application.
 * Each resource corresponds to a specific feature or functionality in the application.
 *
 * @readonly
 * @enum {string}
 * @property {string} DA - Represents the Dashboard feature.
 * @property {string} IN - Represents the Inspections feature.
 * @property {string} RE - Represents the Reports feature.
 * @property {string} US - Represents the Users feature.
 * @property {string} LA - Represents the Locations feature.
 * @property {string} NO - Represents the Notifications feature.
 * @property {string} ES - Represents the Estimates feature.
 * @property {string} WI - Represents the Widgets feature.
 * @property {string} SE - Represents the Settings feature.
 * @property {string} IF - Represents the Inspections Flows feature.
 * @property {string} AD - Represents the Audits feature.
 * @property {string} CR - Represents the Cars feature.
 * @property {string} DV - Represents the Developers feature.
 * @property {string} RS - Represents the Reports summary.
 * @property {string} IFG - Represents the Inspection Flows Groups feature.
 * @property {string} BCI - Represents the Button Create Inspections feature.
 * @property {string} CRD - Represents the Car Damages feature.
 * @property {string} CRC - Represents the Car Claims feature.
 * @property {string} CN - Represents the Contracts feature.
 * @property {string} SC - Represents the Step Constructor feature.
 */
export enum Resource {
  DA = 'DASHBOARD',
  IN = 'INSPECTIONS',
  RE = 'REPORTS',
  US = 'USERS',
  LA = 'LOCATIONS',
  NO = 'NOTIFICATIONS',
  ES = 'ESTIMATES',
  WI = 'WIDGETS',
  SE = 'SETTINGS',
  IF = 'INSPECTIONS_FLOWS',
  AD = 'AUDITS',
  CR = 'CARS',
  DV = 'DEVELOPER',
  RS = 'REPORTS_SUMMARY',
  IFG = 'INSPECTION_FLOWS_GROUPS',
  BCI = 'BUTTON_CREATE_INSPECTIONS',
  CRD = 'CAR_DAMAGES',
  CRC = 'CAR_CLAIMS',
  CN = 'CONTRACTS',
  SC = 'STEP_CONSTRUCTOR',
}

/**
 * How broadly a user may see data inside their company.
 *
 * Orthogonal to {@link UserRole}: the role decides what a user may *do*, this decides
 * *where*. `ALL` is a rule — every location of the company, present and future — so a
 * newly created location is covered with nothing to propagate. `LIST` is an enumeration
 * granted explicitly, one entry of which is active at a time.
 *
 * Defaults to `LIST`. An absent value must never be read as `ALL`.
 *
 * @enum {string}
 * @property {string} ALL - Company-wide: no location predicate and no location switching.
 * @property {string} LIST - An explicit set: queries pin to the active location.
 */
export enum LocationScope {
  ALL = 'ALL',
  LIST = 'LIST',
}

/**
 * The breadth a principal actually gets once role and {@link LocationScope} are both
 * taken into account — what `resolveLocationScope` returns.
 *
 * Distinct from {@link LocationScope}: that is what is stored on the user, this is what
 * a query or an access check acts on. `SUPER_ADMIN` has no stored scope yet resolves to
 * `ALL_TENANTS`, and a company-wide role resolves to `COMPANY` whatever it was given.
 *
 * @enum {string}
 * @property {string} ALL_TENANTS - Platform operator: no tenant restriction at all.
 * @property {string} COMPANY - Company-wide: pin the company, do not pin a location.
 * @property {string} LOCATION - One active location at a time: pin both.
 */
export enum LocationScopeKind {
  ALL_TENANTS = 'ALL_TENANTS',
  COMPANY = 'COMPANY',
  LOCATION = 'LOCATION',
}
